import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // [Archies] This handler only fires for same-product variant changes (size) — colour swaps morph
    // <main> instead. For a size change the media set is identical, so rebuilding the gallery is
    // pointless and it resets the slideshow to slide 0, losing the shopper's carousel position. Skip
    // the replace when the ordered media is unchanged (slide-id = media id), leaving the live gallery
    // untouched — zero rebuild, zero flash, position preserved. Logged in UPGRADE.md.
    const mediaSignature = (g) =>
      Array.from(g.querySelectorAll('slideshow-slide'))
        .map((slide) => slide.getAttribute('slide-id') || '')
        .join(',');

    if (mediaSignature(newMediaGallery) === mediaSignature(this)) {
      // Same media (size change) → don't rebuild. variant-picker disabled the scroller's mandatory
      // scroll-snap and locked the slideshow's resize-recalc for this window, so the reflow can't
      // re-snap the carousel to slide 0. Make sure the scroll is exactly where it was (instant), then
      // — once the price/inventory morph reflows have settled — re-enable snap and release the lock.
      const saved = parseInt(this.dataset.archiesScrollLeft || '', 10);
      const scroller = this.querySelector('slideshow-slides');
      const slideshow = this.querySelector('slideshow-component');
      const setScroll = () => {
        if (saved > 0 && scroller && scroller.scrollLeft !== saved) {
          scroller.style.scrollBehavior = 'auto';
          scroller.scrollLeft = saved;
          scroller.style.scrollBehavior = '';
        }
      };
      setScroll();
      setTimeout(() => {
        setScroll();
        if (scroller) scroller.style.scrollSnapType = ''; // restore CSS 'x mandatory'
        slideshow?.removeAttribute('data-lock-position');
      }, 350);
      return;
    }

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
